import { mapLabelDtoToModel } from '@hotjar/labels';

import type { PaginatedResponse as PaginatedResponseDto } from '../common/dto';
import {
  mapGenericHighlightDtoToModel,
  mapStringToDeviceType,
  mapStringToHeatmapType,
  mapPaginatedResponse,
} from '../common/mapper';
import type { PaginatedResponse as PaginatedResponseModel } from '../common/model';

import type {
  SelectedArea as SelectedAreaDto,
  HeatmapHighlight as HeatmapHighlightDto,
  HeatmapHighlightCreateData as HeatmapHighlightCreateDataDto,
  HeatmapOnlyHighlightsListItem as HeatmapOnlyHighlightsListItemDto,
} from './dto';
import type {
  SelectedArea as SelectedAreaModel,
  HeatmapHighlight as HeatmapHighlightModel,
  HeatmapHighlightCreateData as HeatmapHighlightCreateDataModel,
  HeatmapOnlyHighlightsListItem as HeatmapOnlyHighlightsListItemModel,
} from './model';

export function mapSelectedAreaDtoToModel(dto: SelectedAreaDto): SelectedAreaModel {
  return {
    x1: dto[0],
    y1: dto[1],
    x2: dto[2],
    y2: dto[3],
  };
}

export function mapSelectedAreaModelToDto(model: SelectedAreaModel): SelectedAreaDto {
  return [model.x1, model.y1, model.x2, model.y2];
}

function mapUrlToPage(url: string): string | 'unknown' {
  try {
    const parsed = new URL(url);
    return parsed.pathname;
  } catch (err) {
    return 'unknown';
  }
}

export function mapHighlightDtoToHeatmapHighlightModel(
  dto: HeatmapHighlightDto,
): HeatmapHighlightModel {
  return {
    ...mapGenericHighlightDtoToModel(dto),
    metadata: {
      deviceType: mapStringToDeviceType(dto.metadata.device_type),
      heatmapType: mapStringToHeatmapType(dto.metadata.heatmap_type),
      pagePath: mapUrlToPage(dto.metadata.page_url),
      selectedArea: mapSelectedAreaDtoToModel(dto.metadata.selected_area),
      clicksCount: dto.metadata.clicks_count,
      clicksPercentage: dto.metadata.clicks_percentage,
    },
    type: 'heatmap',
  };
}

export function mapHeatmapHighlightCreateDataModelToDto(
  model: HeatmapHighlightCreateDataModel,
): HeatmapHighlightCreateDataDto {
  const data: HeatmapHighlightCreateDataDto = {
    clicks_count: model.clicksCount,
    clicks_percentage: model.clicksPercentage,
    filters: model.filters,
    image: model.image,
    screenshot_url: model.screenshotUrl,
    selected_area: mapSelectedAreaModelToDto(model.selectedArea),
  };

  return data;
}

export function mapHeatmapOnlyHighlightsListItemDtoToModel(
  dto: HeatmapOnlyHighlightsListItemDto,
): HeatmapOnlyHighlightsListItemModel {
  return {
    author: {
      avatar: dto.user_picture_url,
      id: String(dto.user_id),
      name: dto.user_name,
    },
    clicksCount: dto.metadata.clicks_count,
    clicksPercentage: dto.metadata.clicks_percentage,
    selectedArea: mapSelectedAreaDtoToModel(dto.metadata.selected_area),
    description: dto.description,
    id: String(dto.id),
    labels: dto.labels.map(mapLabelDtoToModel),
  };
}

export function mapHeatmapOnlyHighlightsDtoToModel(
  data: PaginatedResponseDto<HeatmapOnlyHighlightsListItemDto>,
): PaginatedResponseModel<HeatmapOnlyHighlightsListItemModel> {
  return mapPaginatedResponse(mapHeatmapOnlyHighlightsListItemDtoToModel, data);
}
